import React, {useState, useEffect, useRef, useMemo} from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getExpandedRowModel,
    flexRender,
    SortingState,
    PaginationState,
    ExpandedState
} from '@tanstack/react-table';
import {TsDataTableProps, IDataTableItem, DataTableColumnType, DEFAULT_PAGE_SIZE} from './ts-data-table.contracts';
import {resolveColumns} from './ts-data-table.defaults';
import {TsPagination} from './TsPagination';

import './ts-data-table.style.scss';

const TANSTACK_DEFAULT_COLUMN_SIZE = 150;

function toDataItem<T>(row: {original: T}): IDataTableItem<T> {
    return {value: row.original};
}

function isExpandColumn(columnDef: {meta?: unknown}): boolean {
    return (columnDef.meta as any)?.type === DataTableColumnType.ExpandRow;
}

export function TsDataTable<TData>(props: TsDataTableProps<TData>) {
    const {
        data,
        columns,
        totalRows,
        translate,
        pageIndex: controlledPageIndex = 0,
        pageSize = DEFAULT_PAGE_SIZE,
        isLoading = false,
        manualPagination = false,
        onPageChange,
        getRowCanExpand,
        renderSubComponent,
        expandedRowIndex,
        initialSorting,
        onSortingChange
    } = props;

    const isExpandable = !!(getRowCanExpand && renderSubComponent);

    const rawData = useMemo(() => data.map((item) => item.value), [data]);
    const resolvedColumns = useMemo(() => resolveColumns(columns, isExpandable), [columns, isExpandable]);

    const [sorting, setSorting] = useState<SortingState>(
        initialSorting ? [{id: initialSorting.field, desc: initialSorting.desc}] : []
    );
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [page, setPage] = useState<PaginationState>({
        pageIndex: controlledPageIndex,
        pageSize
    });

    const onPageChangeRef = useRef(onPageChange);
    const isInitialMount = useRef(true);

    onPageChangeRef.current = onPageChange;

    const pageCount = manualPagination ? Math.ceil(totalRows / page.pageSize) : undefined;

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;

            return;
        }

        onPageChangeRef.current?.(page);
    }, [page.pageIndex, page.pageSize]);

    useEffect(() => {
        if (page.pageIndex !== controlledPageIndex) {
            setPage((prev) => ({...prev, pageIndex: controlledPageIndex}));
        }
    }, [controlledPageIndex]);

    useEffect(() => {
        if (page.pageSize !== pageSize) {
            setPage({pageIndex: 0, pageSize});
        }
    }, [pageSize]);

    const tableBodyRef = useRef<HTMLTableSectionElement>(null);

    useEffect(() => {
        if (expandedRowIndex != null) {
            setExpanded({[String(expandedRowIndex)]: true});

            requestAnimationFrame(() => {
                const tbody = tableBodyRef.current;
                const row = tbody?.querySelector(`[data-row-index="${expandedRowIndex}"]`) as HTMLElement;

                if (tbody && row) {
                    const scrollTarget = row.offsetTop - 50;

                    tbody.scrollTo({top: Math.max(0, scrollTarget), behavior: 'smooth'});
                }
            });
        }
    }, [expandedRowIndex]);

    useEffect(() => {
        if (onSortingChange) {
            onSortingChange(sorting.length > 0 ? {field: sorting[0].id, desc: sorting[0].desc} : null);
        }
    }, [sorting]);

    const table = useReactTable({
        data: rawData,
        columns: resolvedColumns,
        state: {
            sorting,
            expanded,
            pagination: page
        },
        pageCount,
        manualPagination,
        autoResetPageIndex: false,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
        onPaginationChange: setPage,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: getRowCanExpand ? (row) => getRowCanExpand(toDataItem(row)) : undefined
    });

    return (
        <React.Fragment>
            <div className='ts-data-table-wrapper'>
                <div className={`ts-data-table-container ${isLoading ? 'is-loading' : ''}`}>
                    <table className='ts-data-table'>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        const canSort = header.column.getCanSort();
                                        const sorted = header.column.getIsSorted();

                                        return (
                                            <th
                                                key={header.id}
                                                className={`${canSort ? 'sortable' : ''} ${isExpandColumn(header.column.columnDef) ? 'expander-cell' : ''}`.trim()}
                                                onClick={header.column.getToggleSortingHandler()}
                                                style={{
                                                    flex:
                                                        !isExpandColumn(header.column.columnDef) &&
                                                        header.getSize() !== TANSTACK_DEFAULT_COLUMN_SIZE
                                                            ? header.getSize()
                                                            : undefined
                                                }}>
                                                <div className='th-content'>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext()
                                                          )}
                                                    {canSort && (
                                                        <span className='sort-indicator'>
                                                            {sorted === 'asc'
                                                                ? ' \u25B2'
                                                                : sorted === 'desc'
                                                                  ? ' \u25BC'
                                                                  : ' \u25BD'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody ref={tableBodyRef}>
                            {table.getRowModel().rows.map((row) => (
                                <React.Fragment key={row.id}>
                                    <tr
                                        className={row.getIsExpanded() ? 'expanded' : ''}
                                        data-row-index={row.index}
                                        onClick={() => row.getCanExpand() && row.toggleExpanded()}
                                        style={row.getCanExpand() ? {cursor: 'pointer'} : undefined}>
                                        {row.getVisibleCells().map((cell) => (
                                            <td
                                                key={cell.id}
                                                className={isExpandColumn(cell.column.columnDef) ? 'expander-cell' : ''}
                                                style={{
                                                    flex:
                                                        !isExpandColumn(cell.column.columnDef) &&
                                                        cell.column.getSize() !== TANSTACK_DEFAULT_COLUMN_SIZE
                                                            ? cell.column.getSize()
                                                            : undefined
                                                }}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                    {row.getIsExpanded() && renderSubComponent && (
                                        <tr className='expanded-row'>
                                            <td colSpan={row.getVisibleCells().length}>
                                                {renderSubComponent({row: toDataItem(row)})}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    {isLoading && (
                        <div className='ts-data-table-loading'>
                            <div className='loading-spinner' />
                        </div>
                    )}
                </div>
            </div>
            <div className='ts-pagination-container'>
                <TsPagination table={table} totalRows={totalRows} translate={translate} />
            </div>
        </React.Fragment>
    );
}
